import type { RankingPrediction, RankingFactor } from "@/types/analysis";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, AlertTriangle, Target } from "lucide-react";

const weightConfig = {
  high: { label: "High Impact", className: "bg-destructive/10 text-destructive border-destructive/20" },
  medium: { label: "Medium Impact", className: "bg-warning/10 text-warning border-warning/20" },
  low: { label: "Low Impact", className: "bg-muted text-muted-foreground border-border" },
};

const getBarColor = (score: number) => {
  if (score < 40) return "bg-destructive";
  if (score < 70) return "bg-warning";
  return "bg-success";
};

const sortFactors = (factors: RankingFactor[]) => {
  const potentialOrder = { high: 0, medium: 1, low: 2 };
  return [...factors].sort((a, b) => {
    const potDiff = potentialOrder[a.improvementPotential] - potentialOrder[b.improvementPotential];
    if (potDiff !== 0) return potDiff;
    return a.score - b.score;
  });
};

export const RankingTab = ({ data }: { data: RankingPrediction }) => (
  <div className="space-y-4 mt-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="border-border bg-card">
        <CardHeader><CardTitle className="text-base">Current Estimated Range</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold text-warning">#{data.currentRangeMin}</span>
            <span className="text-2xl text-muted-foreground">–</span>
            <span className="text-4xl font-bold text-warning">#{data.currentRangeMax}</span>
          </div>
          <div className="mt-4 h-3 bg-secondary rounded-full overflow-hidden relative">
            <div
              className="absolute h-full bg-warning/50 rounded-full"
              style={{ left: `${(data.currentRangeMin / 100) * 100}%`, width: `${((data.currentRangeMax - data.currentRangeMin) / 100) * 100}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>#1</span><span>#50</span><span>#100+</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border-primary/20 bg-primary/5">
        <CardHeader><CardTitle className="text-base">Achievable Range (After Improvements)</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold text-primary">#{data.achievableRangeMin}</span>
            <span className="text-2xl text-muted-foreground">–</span>
            <span className="text-4xl font-bold text-primary">#{data.achievableRangeMax}</span>
          </div>
          <div className="mt-4 h-3 bg-secondary rounded-full overflow-hidden relative">
            <div
              className="absolute h-full bg-primary/50 rounded-full"
              style={{ left: `${(data.achievableRangeMin / 100) * 100}%`, width: `${((data.achievableRangeMax - data.achievableRangeMin) / 100) * 100}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>#1</span><span>#50</span><span>#100+</span>
          </div>
        </CardContent>
      </Card>
    </div>

    <Card className="border-border bg-card">
      <CardContent className="p-4 flex items-center gap-2">
        <Badge variant="outline" className="font-mono">Confidence: {data.confidence}%</Badge>
      </CardContent>
    </Card>

    {data.categoryBreakdown && data.categoryBreakdown.length > 0 && (
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="w-4 h-4 text-primary" /> Ranking Factor Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {sortFactors(data.categoryBreakdown).map((factor, i) => {
            const weight = weightConfig[factor.weight];
            return (
              <div key={i} className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium truncate">{factor.category}</span>
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 shrink-0 ${weight.className}`}>
                      {weight.label}
                    </Badge>
                    {factor.improvementPotential === "high" && (
                      <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5 py-0 shrink-0">
                        Focus Here
                      </Badge>
                    )}
                  </div>
                  <span className="text-sm font-bold tabular-nums shrink-0">{factor.score}</span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${getBarColor(factor.score)}`}
                    style={{ width: `${factor.score}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">{factor.summary}</p>
              </div>
            );
          })}
        </CardContent>
      </Card>
    )}

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="border-border bg-card">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-success" /> Key Ranking Drivers</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {data.drivers.map((d, i) => (
            <div key={i} className="text-sm flex items-start gap-2 p-2 rounded bg-success/5 text-foreground">
              <TrendingUp className="w-3.5 h-3.5 text-success mt-0.5 shrink-0" />{d}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-border bg-card">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingDown className="w-4 h-4 text-destructive" /> Key Barriers</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {data.barriers.map((b, i) => (
            <div key={i} className="text-sm flex items-start gap-2 p-2 rounded bg-destructive/5 text-foreground">
              <TrendingDown className="w-3.5 h-3.5 text-destructive mt-0.5 shrink-0" />{b}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>

    <Card className="border-warning/20 bg-warning/5">
      <CardContent className="p-4 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-warning mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground">{data.disclaimer}</p>
      </CardContent>
    </Card>
  </div>
);
